/** Compare cap from section settings; toggles `.compare-table--at-limit` for CSS (no alerts). */
function isCompareLimitReached() {
  const container = document.querySelector('.compare-products');
  let sectionSettings = {};

  if (container?.dataset.sectionSettings) {
    try {
      sectionSettings = JSON.parse(container.dataset.sectionSettings);
    } catch {
      const table = document.querySelector('.compare-table');
      if (table) table.classList.remove('compare-table--at-limit');
      return { atLimit: false, max: 0, count: 0 };
    }
  }

  const maxItemsFromSettings = parseInt(sectionSettings.compare_max_items, 10);
  const max = Number.isNaN(maxItemsFromSettings) || maxItemsFromSettings < 0 ? 0 : maxItemsFromSettings;
  const count = document.querySelectorAll('.compare-table .compare-table__heading--product')?.length ?? 0;
  const atLimit = max > 0 && count >= max;

  const table = document.querySelector('.compare-table');
  if (table) table.classList.toggle('compare-table--at-limit', atLimit);

  return { atLimit, max, count };
}
class ProductComparisonTable {
  constructor() {
    this.container = document.querySelector('.compare-products');
    if (!this.container) return;

    this.compareTable = this.container.querySelector('.compare-table');
    this.tableWrapper = this.container.querySelector('.compare-table-wrapper');
    
    // Mobile slider state
    this.currentSlideIndex = 0;
    this.totalSlides = 0;
    
    // Safely parse section settings
    try {
      this.settings = JSON.parse(this.container.dataset.sectionSettings || '{}');
    } catch (error) {
      console.error('Error parsing section settings:', error);
      console.log('Raw settings:', this.container.dataset.sectionSettings);
      this.settings = {};
    }

    // Merged namespace.key from Liquid (section + theme fallback); section JSON alone can omit it.
    this.variantSpecsRefFromDom = (this.container.dataset.variantSpecsRef || '').trim();
    this.compareEmbeddedVariantSpecs = this.loadCompareEmbeddedVariantSpecs();

    this.init();
  }

  init() {
    // Set up event listeners
    this.setupEventListeners();
    
    // Initialize mobile table slider
    this.initMobileTableSlider();
        
    // If Liquid left tbody empty, build rows from seed (same JSON as pdp--variant-specs-json-value for the PDP variant)
    this.hydrateSpecsRowsFromSeedIfEmpty();
    setTimeout(() => this.hydrateSpecsRowsFromSeedIfEmpty(), 0);
    setTimeout(() => this.hydrateSpecsRowsFromSeedIfEmpty(), 250);
    setTimeout(() => this.updateCompareRowVisibility(), 300);
    document.addEventListener('shopify:section:load', () => {
      this.hydrateSpecsRowsFromSeedIfEmpty();
      this.ensureMobileAddButtonsOnProductHeaders();
      this.updateCompareRowVisibility();
    });

    isCompareLimitReached();
    this.ensureMobileAddButtonsOnProductHeaders();
    this.initMatchCompareProductTitleHeights();
  }

  canShowMobileAddButton() {
    const collection = this.settings.add_products_collection;
    const hasCollection =
      collection != null && collection !== '' && collection !== false;
    return hasCollection && this.settings.show_compare_button !== false;
  }

  getMobileInlineAddButtonHtml() {
    if (!this.canShowMobileAddButton()) return '';
    return `<button type="button" class="compare-table__add-button compare-table__add-button--mobile-inline" aria-label="Select a product to compare">
      <span class="compare-table__add-button-icon" aria-hidden="true">+</span>
      <span class="compare-table__add-button-text">Select a Product to Compare</span>
    </button>`;
  }

  /** Mobile CTA below price on every product column (Liquid + JS-added columns). */
  ensureMobileAddButtonsOnProductHeaders() {
    if (!this.compareTable) return;
    const buttonHtml = this.getMobileInlineAddButtonHtml();
    if (!buttonHtml) return;

    this.compareTable
      .querySelectorAll('.compare-table__heading--product')
      .forEach((th) => {
        const header = th.querySelector('.compare-table__product-header');
        if (!header || header.querySelector('.compare-table__add-button--mobile-inline')) {
          return;
        }
        header.insertAdjacentHTML('beforeend', buttonHtml);
      });
  }

  initMatchCompareProductTitleHeights() {
    this.matchCompareProductTitleHeights();
    requestAnimationFrame(() => this.matchCompareProductTitleHeights());

    if (this._matchTitleHeightsResizeBound) return;
    this._matchTitleHeightsResizeBound = true;

    this._debounceMatchTitleHeights = () => {
      window.clearTimeout(this._matchTitleHeightsTimeout);
      this._matchTitleHeightsTimeout = window.setTimeout(() => {
        this.matchCompareProductTitleHeights();
      }, 100);
    };

    window.addEventListener('resize', this._debounceMatchTitleHeights);

    if (this.compareTable && typeof ResizeObserver !== 'undefined') {
      this._compareTitleResizeObserver = new ResizeObserver(() => {
        this._debounceMatchTitleHeights();
      });
      this._compareTitleResizeObserver.observe(this.compareTable);
    }

    this.bindCompareProductImageLoadForTitleHeights();
  }

  bindCompareProductImageLoadForTitleHeights() {
    if (!this.compareTable) return;

    this.compareTable.querySelectorAll('.compare-table__product-image').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => this.matchCompareProductTitleHeights(), { once: true });
    });
  }

  matchCompareProductTitleHeights() {
    if (!this.compareTable) return;

    const titles = this.compareTable.querySelectorAll(
      '.compare-table__heading--product .compare-table__product-title'
    );

    titles.forEach((title) => {
      title.style.minHeight = '';
    });

    if (!window.matchMedia('(min-width: 768px)').matches || titles.length < 2) {
      return;
    }

    let maxHeight = 0;
    titles.forEach((title) => {
      maxHeight = Math.max(maxHeight, title.offsetHeight);
    });

    if (maxHeight <= 0) return;

    titles.forEach((title) => {
      title.style.minHeight = `${maxHeight}px`;
    });
  }

  // Set up event listeners for remove buttons and add to cart
  setupEventListeners() {

    // Use event delegation for compare table remove buttons (desktop/table view)
    if (this.compareTable) {
      this.compareTable.addEventListener('click', (event) => {
        const removeButton = event.target.closest('.compare-table__remove-button');
        if (removeButton) {
          this.removeProductFromTable(removeButton);
        }
      });
    }

    // Mobile table slider pagination dots
    if (this.tableWrapper) {
      const paginationContainer = this.tableWrapper.querySelector('.compare-table-pagination');
      if (paginationContainer) {
        paginationContainer.addEventListener('click', (event) => {
          const dot = event.target.closest('.slider-pagination__item');
          if (dot) {
            const slideIndex = parseInt(dot.dataset.slideIndex, 10);
            this.goToSlide(slideIndex);
          }
        });
      }
    }

    // Listen for add product event from modal
    document.addEventListener('compare:add-product', (event) => {
      const { variantId, handle } = event.detail || {};
      if (variantId && handle) {
        this.addVariantToTable(String(variantId), handle);
      }
    });

    this.bindBuyBoxToCompareCurrentColumn();
  }

  /**
   * When the PDP buy box updates #product-select (variant change), refresh the compare table
   * "current" column header + spec cells for that product.
   */
  bindBuyBoxToCompareCurrentColumn() {
    if (this._buyBoxVariantListenerBound) return;
    this._buyBoxVariantListenerBound = true;

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || target.id !== 'product-select') return;
      if (!this.compareTable) return;

      const th = this.compareTable.querySelector('.compare-table__heading--current');
      if (!th) return;

      const handle = th.dataset.productHandle;
      if (!handle) return;

      const variantId = String(target.value || '').trim();
      if (!variantId) return;

      this.syncCompareCurrentColumnFromBuyBox(variantId, th).catch((err) => console.error(err));
    });
  }

  async fetchProductJson(handle) {
    const handleTrimmed = String(handle || '').trim();
    if (!handleTrimmed) return null;
    const res = await fetch(`/products/${encodeURIComponent(handleTrimmed)}.json`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.product || null;
  }

  updateCompareCurrentColumnHeader(th, product, variant, column) {
    th.dataset.variantId = String(variant.id);

    const img = th.querySelector('.compare-table__product-image');
    if (img && column?.image) {
      img.src = column.image;
    }

    th.querySelectorAll('.compare-table__product-link').forEach((link) => {
      link.href = `/products/${product.handle}?variant=${variant.id}`;
    });

    const titleBlock = th.querySelector('.compare-table__product-title');
    if (titleBlock) {
      const base = String(column.compareHeaderBase || product.title || '').trim();
      if (product.variants && product.variants.length > 1) {
        titleBlock.innerHTML = `${this.escapeHtmlCompare(base)} <span class="compare-table__variant-title">- ${this.escapeHtmlCompare(String(variant.title || '').trim())}</span>`;
      } else {
        titleBlock.textContent = base;
      }
    }

    const titleLink = th.querySelector('.compare-table__product-link--title');
    if (titleLink) {
      const priceText = this.formatCompareVariantPrice(column);
      let priceEl = titleLink.querySelector('.compare-table__product-price');
      if (priceText) {
        if (!priceEl) {
          priceEl = document.createElement('p');
          priceEl.className = 'compare-table__product-price rte';
          titleLink.appendChild(priceEl);
        }
        priceEl.textContent = priceText;
      } else if (priceEl) {
        priceEl.remove();
      }
    }

    // Update dimensions link visibility and URL
    const productHeader = th.querySelector('.compare-table__product-header');
    let dimLink = th.querySelector('.compare-table__dimensions-link');
    if (column.dimensionsImageUrl) {
      if (dimLink) {
        dimLink.removeAttribute('hidden');
        dimLink.dataset.dimensionsImageUrl = column.dimensionsImageUrl;
      } else if (productHeader) {
        dimLink = document.createElement('button');
        dimLink.type = 'button';
        dimLink.className = 'compare-table__dimensions-link card-product__remove-button';
        dimLink.setAttribute('data-dimensions-trigger', '');
        dimLink.setAttribute('data-modal-open', 'dimensions-image-modal');
        dimLink.dataset.dimensionsImageUrl = column.dimensionsImageUrl;
        dimLink.dataset.dimensionsImageAlt = `${column.compareHeaderBase || column.title || ''} Dimensions`;
        dimLink.textContent = 'Product Dimensions';
        productHeader.appendChild(dimLink);
      }
    } else if (dimLink) {
      dimLink.setAttribute('hidden', '');
    }

    requestAnimationFrame(() => this.matchCompareProductTitleHeights());
  }

  async syncCompareCurrentColumnFromBuyBox(variantId, th) {
    const handle = th.dataset.productHandle;
    const product = await this.fetchProductJson(handle);
    if (!product?.variants?.length) return;

    const variant = product.variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    const metafieldsData = await this.fetchCompareMediaAndMetafields(product, handle);
    const column = this.buildColumnPayload(product, variant, metafieldsData);

    this.updateCompareCurrentColumnHeader(th, product, variant, column);

    const columnIndex = parseInt(th.dataset.columnIndex, 10);
    if (Number.isNaN(columnIndex)) return;

    const specRows = this.getVariantSpecsRowsForId(variantId, metafieldsData);
    if (specRows?.length) {
      this.renderCompareTbodySpecRows(specRows);
      const seedEl = this.container?.querySelector('script[data-compare-specs-seed]');
      if (seedEl) seedEl.textContent = JSON.stringify(specRows);
      await this.refreshNonCurrentCompareColumns(columnIndex);
      this.updateCompareRowVisibility();
      return;
    }

    const metafieldsConfig = this.parseMetafieldsConfig();
    this.compareTable.querySelectorAll('tbody tr.compare-table__row').forEach((row) => {
      const td = row.querySelector(
        `td.compare-table__cell--value.compare-table__cell--current[data-column-index="${columnIndex}"]`
      );
      if (!td) return;
      const labelCell = row.querySelector('.compare-table__cell--label, .compare-table__cell--sticky');
      const labelSpan = labelCell?.querySelector('span:first-child');
      const rowLabel = labelSpan?.textContent?.trim() || '';
      td.innerHTML = this.getCellContent(rowLabel, column, metafieldsConfig) || '&nbsp;';
    });
    this.updateCompareRowVisibility();
  }

  async refreshNonCurrentCompareColumns(skipColumnIndex) {
    if (!this.compareTable) return;
    const headers = this.compareTable.querySelectorAll('.compare-table__heading--product');
    const metafieldsConfig = this.parseMetafieldsConfig();

    for (const header of headers) {
      const idx = parseInt(header.dataset.columnIndex, 10);
      if (Number.isNaN(idx) || idx === skipColumnIndex) continue;

      const productHandle = header.dataset.productHandle;
      const headerVariantId = header.dataset.variantId;
      if (!productHandle || !headerVariantId) continue;

      try {
        const product = await this.fetchProductJson(productHandle);
        if (!product?.variants?.length) continue;
        const headerVariant =
          product.variants.find((v) => String(v.id) === String(headerVariantId)) || product.variants[0];
        const metafieldsData = await this.fetchCompareMediaAndMetafields(product, productHandle);
        const column = this.buildColumnPayload(product, headerVariant, metafieldsData);

        this.compareTable.querySelectorAll('tbody tr.compare-table__row').forEach((row) => {
          const td = row.querySelector(`td.compare-table__cell--value[data-column-index="${idx}"]`);
          if (!td) return;
          const labelCell = row.querySelector('.compare-table__cell--label, .compare-table__cell--sticky');
          const labelSpan = labelCell?.querySelector('span:first-child');
          const rowLabel = labelSpan?.textContent?.trim() || '';
          td.innerHTML = this.getCellContent(rowLabel, column, metafieldsConfig) || '&nbsp;';
        });
      } catch {
        /* keep placeholders */
      }
    }

    const availRow = this.compareTable.querySelector('tr.compare-table__row--available-options');
    if (availRow) {
      await this.populateAvailableOptionsRow(availRow, headers);
    }
  }

  isCompareCellEmpty(td) {
    if (!td) return true;
    const rte = td.querySelector('.rte');
    const text = (rte ? rte.textContent : td.textContent) || '';
    return text.replace(/\u00a0/g, ' ').trim().length === 0;
  }

  compareRowHasValues(row) {
    if (!row) return false;
    const valueCells = row.querySelectorAll('td.compare-table__cell--value');
    return Array.from(valueCells).some((td) => !this.isCompareCellEmpty(td));
  }

  updateCompareRowVisibility() {
    if (!this.compareTable) return;
    this.compareTable.querySelectorAll('tbody tr.compare-table__row').forEach((row) => {
      const hasValue = this.compareRowHasValues(row);
      row.hidden = !hasValue;
      row.classList.toggle('compare-table__row--hidden', !hasValue);
    });
  }

  getCompareColumnImageUrl(product, variant, metafieldsData) {
    let url = '';
    const variantId = variant?.id != null ? String(variant.id) : '';

    // Liquid alternate template map is authoritative across Direct/Pro/EU
    if (metafieldsData?.compareVariantMedia && variantId) {
      const mediaMap = metafieldsData.compareVariantMedia;
      url = mediaMap[variantId] || '';
    }

    // Prefer first carousel image from variant custom.images (Storefront metafields)
    if (!url && metafieldsData?.variants?.edges?.length && variantId) {
      const variantGid = (gid) => {
        if (!gid) return '';
        const s = String(gid);
        return s.includes('/') ? s.split('/').pop() : s;
      };
      for (const edge of metafieldsData.variants.edges) {
        const node = edge?.node;
        if (!node || variantGid(node.id) !== variantId) continue;
        const mf = node.metafields?.find((m) => m?.namespace === 'custom' && m.key === 'images');
        const refNode = mf?.references?.edges?.[0]?.node;
        url = refNode?.image?.url || refNode?.url || '';
        if (url) break;
      }
    }

    // Last resort: product media gallery (not featured/cropped card image)
    if (!url) {
      const images = product?.images || [];
      if (images.length) {
        const first = images[0];
        url = typeof first === 'string' ? first : first?.src || '';
      }
    }

    if (!url || typeof url !== 'string') return url;
    if (url.startsWith('//')) url = `https:${url}`;
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    if (!/(?:shopify(?:cdn)?\.com|\/cdn\/shop\/)/i.test(url)) return url;

    const width = 960;
    if (/[?&]width=\d+/.test(url)) {
      return url.replace(/[?&]width=\d+/, (m) => m.replace(/=\d+$/, `=${width}`));
    }
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  parseMarkedJsonMap(text, marker) {
    if (!text || !marker) return {};
    const open = `<!--${marker}-->`;
    const close = `<!--/${marker}-->`;
    let start = text.indexOf(open);
    let end = text.indexOf(close);
    let jsonText = '';
    if (start !== -1 && end !== -1 && end > start) {
      jsonText = text.slice(start + open.length, end).trim();
    } else {
      // Fallback when markers are stripped: first top-level object only.
      start = text.indexOf('{');
      end = text.indexOf('}', start);
      if (start === -1 || end === -1) return {};
      // Walk to matching closing brace for a shallow object map.
      let depth = 0;
      end = -1;
      for (let i = start; i < text.length; i++) {
        const ch = text[i];
        if (ch === '{') depth += 1;
        else if (ch === '}') {
          depth -= 1;
          if (depth === 0) {
            end = i;
            break;
          }
        }
      }
      if (end === -1) return {};
      jsonText = text.slice(start, end + 1);
    }
    try {
      const parsed = JSON.parse(jsonText);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  async fetchCompareAlternateJsonMap(handle, view, marker, cacheKey) {
    const handleTrimmed = String(handle || '').trim();
    if (!handleTrimmed) return {};
    if (!this[cacheKey]) this[cacheKey] = new Map();
    if (this[cacheKey].has(handleTrimmed)) {
      return this[cacheKey].get(handleTrimmed);
    }

    try {
      const res = await fetch(
        `/products/${encodeURIComponent(handleTrimmed)}?view=${encodeURIComponent(view)}`
      );
      if (!res.ok) {
        this[cacheKey].set(handleTrimmed, {});
        return {};
      }
      const map = this.parseMarkedJsonMap(await res.text(), marker);
      this[cacheKey].set(handleTrimmed, map);
      return map;
    } catch (error) {
      console.error(`Error fetching compare ${view}:`, error);
      this[cacheKey].set(handleTrimmed, {});
      return {};
    }
  }

  fetchCompareVariantMedia(handle) {
    return this.fetchCompareAlternateJsonMap(
      handle,
      'compare-variant-media',
      'compare-variant-media',
      '_compareVariantMediaCache'
    );
  }

  fetchCompareVariantSpecs(handle) {
    return this.fetchCompareAlternateJsonMap(
      handle,
      'compare-variant-specs',
      'compare-variant-specs',
      '_compareVariantSpecsCache'
    );
  }

  fetchCompareVariantDimensions(handle) {
    return this.fetchCompareAlternateJsonMap(
      handle,
      'compare-variant-dimensions',
      'compare-variant-dimensions',
      '_compareVariantDimensionsCache'
    );
  }

  async fetchCompareMediaAndMetafields(product, handle) {
    const productHandle = handle || product.handle;
    const [metafieldsData, compareVariantMedia, compareVariantSpecs, compareVariantDimensions] =
      await Promise.all([
        this.fetchProductMetafields(product.id),
        this.fetchCompareVariantMedia(productHandle),
        this.fetchCompareVariantSpecs(productHandle),
        this.fetchCompareVariantDimensions(productHandle),
      ]);
    const merged = metafieldsData && typeof metafieldsData === 'object' ? { ...metafieldsData } : {};
    merged.compareVariantMedia = compareVariantMedia || {};
    merged.compareVariantSpecs = compareVariantSpecs || {};
    merged.compareVariantDimensions = compareVariantDimensions || {};
    return merged;
  }

  extractProductCompareNameBeginning(metafieldsData) {
    if (!metafieldsData?.metafields?.length) return '';
    const mf = metafieldsData.metafields.find(
      (m) => m && m.namespace === 'custom' && m.key === 'product_name_beginning'
    );
    if (mf?.value == null || mf.value === '') return '';
    return String(mf.value).trim();
  }

  getVariantDimensionsImageUrl(product, variant, metafieldsData) {
    const want = variant?.id != null ? String(variant.id) : '';
    if (!want) return '';

    // Liquid alternate template map is authoritative across Direct/Pro/EU
    const liquidUrl = metafieldsData?.compareVariantDimensions?.[want];
    if (liquidUrl && typeof liquidUrl === 'string') return liquidUrl;

    if (!metafieldsData?.variants?.edges?.length) return '';
    const gidTail = (gid) => {
      if (!gid) return '';
      const s = String(gid);
      return s.includes('/') ? s.split('/').pop() : s;
    };
    const withCdnTransform = (url) => {
      if (!url) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}width=1200&format=png`;
    };
    for (const edge of metafieldsData.variants.edges) {
      const node = edge?.node;
      if (!node || gidTail(node.id) !== want) continue;
      const mf = node.metafields?.find((m) => m?.namespace === 'custom' && m.key === 'dimensions_image');
      if (!mf) break;
      // single file_reference → `reference`; list.file_reference → `references`
      if (mf.reference?.image?.url) return withCdnTransform(mf.reference.image.url);
      if (mf.reference?.url) return mf.reference.url;
      const refNode = mf?.references?.edges?.[0]?.node;
      if (refNode?.image?.url) return withCdnTransform(refNode.image.url);
      if (refNode?.url) return refNode.url;
      break;
    }
    return '';
  }

  buildColumnPayload(product, variant, metafieldsData) {
    const variants = product?.variants || [];
    const hasOnlyDefaultVariant = variants.length <= 1;
    const vid = String(variant.id);
    const sku = variant.sku || '';
    const variantTitle = variant.title || '';
    const image = this.getCompareColumnImageUrl(product, variant, metafieldsData);
    const dimensionsImageUrl = this.getVariantDimensionsImageUrl(product, variant, metafieldsData);
    const url = `/products/${product.handle}?variant=${variant.id}`;
    const nameBeginning = this.extractProductCompareNameBeginning(metafieldsData);
    const compareHeaderBase = String(nameBeginning || product.title || '').trim();
    return {
      id: product.id,
      variantId: vid,
      preferredVariantId: vid,
      sku,
      handle: product.handle,
      title: product.title,
      compareHeaderBase,
      variantTitle: String(variantTitle).trim(),
      hasOnlyDefaultVariant,
      image,
      dimensionsImageUrl,
      url,
      price: variant.price || product.price,
      compareAtPrice: variant.compare_at_price,
      vendor: product.vendor,
      available: product.available,
      productData: product,
      metafieldsData
    };
  }

  /** Add a column for a specific variant (numeric variant ID). */
  async addVariantToTable(variantId, handle) {
    try {
      const vid = String(variantId).trim();
      const productHandle = String(handle || '').trim();
      if (!vid || !productHandle) return;

      if (isCompareLimitReached().atLimit) {
        return;
      }

      if (this.compareTable) {
        const dup = Array.from(this.compareTable.querySelectorAll('[data-variant-id]')).some(
          (el) => el.dataset.variantId === vid
        );
        if (dup) return;
      }

      const product = await this.fetchProductJson(productHandle);
      if (!product?.variants?.length) return;

      const variant = product.variants.find((v) => String(v.id) === vid);
      if (!variant) {
        console.error('Variant not on product JSON:', vid, productHandle);
        return;
      }

      const metafieldsData = await this.fetchCompareMediaAndMetafields(product, productHandle);
      this.addColumnToTable(this.buildColumnPayload(product, variant, metafieldsData));
    } catch (error) {
      console.error('Error adding variant to table:', error);
    }
  }

  addColumnToTable(column) {
    if (!this.compareTable) return false;

    if (isCompareLimitReached().atLimit) {
      return false;
    }

    const existingColumns = this.compareTable.querySelectorAll('.compare-table__heading--product');
    const newColumnIndex = existingColumns.length;

    const headerRow = this.compareTable.querySelector('thead tr:first-child');
    const addButtonTh = headerRow.querySelector('.compare-table__heading--add');

    const headerBase = String(column.compareHeaderBase || column.title || '').trim();
    const variantSubtitle =
      !column.hasOnlyDefaultVariant && column.variantTitle
        ? ` <span class="compare-table__variant-title">- ${this.escapeHtmlCompare(String(column.variantTitle).trim())}</span>`
        : '';

    const priceDisplay = this.formatCompareVariantPrice(column);
    const priceBlock = priceDisplay
      ? `<p class="compare-table__product-price rte">${this.escapeHtmlCompare(priceDisplay)}</p>`
      : '';

    const dimensionsLinkBlock = column.dimensionsImageUrl
      ? `<button type="button" class="compare-table__dimensions-link card-product__remove-button" data-dimensions-trigger data-modal-open="dimensions-image-modal" data-dimensions-image-url="${this.escapeHtmlCompare(column.dimensionsImageUrl)}" data-dimensions-image-alt="${this.escapeHtmlCompare(headerBase)} Dimensions">Product Dimensions</button>`
      : '';

    const newHeaderCell = document.createElement('th');
    newHeaderCell.className = 'compare-table__heading compare-table__heading--product';
    newHeaderCell.dataset.columnIndex = String(newColumnIndex);
    newHeaderCell.dataset.variantId = column.variantId;
    newHeaderCell.dataset.productHandle = column.handle;
    newHeaderCell.innerHTML = `
      <div class="compare-table__product-header">
        <a href="${column.url}" class="compare-table__product-link card-product__inner">
          <div class="card-product__image-wrapper">
            <img class="compare-table__product-image card-product__image" src="${column.image}" alt="${this.escapeHtmlCompare(headerBase)}${variantSubtitle ? ` - ${this.escapeHtmlCompare(column.variantTitle)}` : ''}" width="200" height="200">
          </div>
        </a>
        <div class="compare-table__product-actions">
          <a href="${column.url}" class="card-product__remove-button compare-table__view-product">View product</a>
          <button type="button" class="compare-table__remove-button card-product__remove-button" data-product-id="${column.id}" data-variant-id="${column.variantId}" data-product-handle="${this.escapeHtmlCompare(column.handle)}">Remove</button>
        </div>
        <a href="${column.url}" class="compare-table__product-link card-product__inner compare-table__product-link--title">
          <p class="compare-table__product-title card-product__title">${this.escapeHtmlCompare(headerBase)}${variantSubtitle}</p>
          ${priceBlock}
        </a>
        ${dimensionsLinkBlock}
        ${this.getMobileInlineAddButtonHtml()}
      </div>
    `;

    headerRow.insertBefore(newHeaderCell, addButtonTh);

    const metafieldsConfig = this.parseMetafieldsConfig();

    const bodyRows = this.compareTable.querySelectorAll('tbody tr');
    bodyRows.forEach((row) => {
      const labelCell = row.querySelector('.compare-table__cell--label, .compare-table__cell--sticky');
      const labelSpan = labelCell?.querySelector('span:first-child');
      const rowLabel = labelSpan?.textContent?.trim() || labelCell?.textContent?.split('?')[0]?.trim() || '';

      const emptyCell = row.querySelector('.compare-table__cell--empty');
      const newCell = document.createElement('td');
      newCell.className = 'compare-table__cell compare-table__cell--value card-product__specs-table-row-cell';
      newCell.dataset.columnIndex = String(newColumnIndex);

      const cellContent = this.getCellContent(rowLabel, column, metafieldsConfig);
      newCell.innerHTML = cellContent || '&nbsp;';

      row.insertBefore(newCell, emptyCell);
    });

    this.addPaginationDot();

    this.totalSlides = this.compareTable.querySelectorAll('.compare-table__heading--product').length;
    this.reindexColumns();
    this.updateMobileNavigation();

    if (this.totalSlides > 0) {
      if (this.currentSlideIndex >= this.totalSlides) {
        this.currentSlideIndex = this.totalSlides - 1;
      }
      this.goToSlide(this.currentSlideIndex);
    }
    isCompareLimitReached();
    this.ensureMobileAddButtonsOnProductHeaders();
    this.bindCompareProductImageLoadForTitleHeights();
    requestAnimationFrame(() => this.matchCompareProductTitleHeights());
    this.updateCompareRowVisibility();
    return true;
  }

  // Add a pagination dot
  addPaginationDot() {
    if (!this.tableWrapper) return;

    const paginationContainer = this.tableWrapper.querySelector('.compare-table-pagination');
    if (!paginationContainer) return;

    const existingDots = paginationContainer.querySelectorAll('.slider-pagination__item');
    const newIndex = existingDots.length;

    const newDot = document.createElement('button');
    newDot.className = 'slider-pagination__item';
    newDot.dataset.slideIndex = newIndex;
    newDot.setAttribute('aria-label', `Go to product ${newIndex + 1}`);
    
    paginationContainer.appendChild(newDot);
  }

  // Initialize mobile table slider
  initMobileTableSlider() {
    if (!this.compareTable) return;

    // Count product columns
    const productColumns = this.compareTable.querySelectorAll('.compare-table__heading--product');
    this.totalSlides = productColumns.length;

    // Set initial active state (first column)
    this.goToSlide(0);
  }

  // Navigate to a specific slide
  goToSlide(index) {
    if (!this.compareTable || this.totalSlides === 0) return;

    // Clamp index to valid range
    if (index < 0) index = 0;
    if (index >= this.totalSlides) index = this.totalSlides - 1;

    this.currentSlideIndex = index;

    // Update active class on all columns
    const allHeaders = this.compareTable.querySelectorAll('.compare-table__heading--product');
    const allCells = this.compareTable.querySelectorAll('.compare-table__cell--value');

    allHeaders.forEach((header, i) => {
      header.classList.toggle('is-active', i === index);
    });

    allCells.forEach((cell) => {
      const cellIndex = parseInt(cell.dataset.columnIndex, 10);
      cell.classList.toggle('is-active', cellIndex === index);
    });

    // Update navigation buttons and counter
    this.updateMobileNavigation();
  }

  // Update mobile pagination dots
  updateMobileNavigation() {
    if (!this.tableWrapper) return;

    const dots = this.tableWrapper.querySelectorAll('.compare-table-pagination .slider-pagination__item');
    dots.forEach((dot, i) => {
      dot.classList.toggle('slider-pagination__item--active', i === this.currentSlideIndex);
    });
  }

  // Remove a compare column (variant) from the compare table
  removeProductFromTable(removeButton) {
    const headerCell = removeButton.closest('th');
    
    if (!headerCell) return;
    
    // Get the column index (account for the sticky label column at index 0)
    const headerRow = headerCell.parentElement;
    const columnIndex = Array.from(headerRow.children).indexOf(headerCell);
    
    // Remove the header cell
    headerCell.remove();
    
    // Remove the corresponding cell from each body row
    const bodyRows = this.compareTable.querySelectorAll('tbody tr');
    bodyRows.forEach((row) => {
      const cells = row.children;
      if (cells[columnIndex]) {
        cells[columnIndex].remove();
      }
    });

    // Update total slides count
    this.totalSlides = this.compareTable.querySelectorAll('.compare-table__heading--product').length;

    // Remove a pagination dot
    this.removePaginationDot();

    // Reindex remaining columns and dots
    this.reindexColumns();

    // Adjust current slide index if needed
    if (this.currentSlideIndex >= this.totalSlides) {
      this.currentSlideIndex = Math.max(0, this.totalSlides - 1);
    }

    // Update slide to show correct product
    this.goToSlide(this.currentSlideIndex);

    isCompareLimitReached();
    requestAnimationFrame(() => this.matchCompareProductTitleHeights());
    this.updateCompareRowVisibility();
  }

  // Remove a pagination dot
  removePaginationDot() {
    if (!this.tableWrapper) return;
    
    const paginationContainer = this.tableWrapper.querySelector('.compare-table-pagination');
    if (paginationContainer) {
      const dots = paginationContainer.querySelectorAll('.slider-pagination__item');
      if (dots.length > 0) {
        // Remove the last dot
        dots[dots.length - 1].remove();
      }
    }
  }

  // Reindex column data attributes and pagination dots after removal
  reindexColumns() {
    const headers = this.compareTable.querySelectorAll('.compare-table__heading--product');
    headers.forEach((header, i) => {
      header.dataset.columnIndex = i;
    });

    const bodyRows = this.compareTable.querySelectorAll('tbody tr');
    bodyRows.forEach((row) => {
      const cells = row.querySelectorAll('.compare-table__cell--value');
      cells.forEach((cell, i) => {
        cell.dataset.columnIndex = i;
      });
    });

    // Reindex pagination dots
    if (this.tableWrapper) {
      const dots = this.tableWrapper.querySelectorAll('.compare-table-pagination .slider-pagination__item');
      dots.forEach((dot, i) => {
        dot.dataset.slideIndex = i;
      });
    }
  }

  // Parse metafields configuration from settings
  parseMetafieldsConfig() {
    const config = {};
    if (!this.settings.metafields) return config;

    const rows = this.settings.metafields.split(/\n+/).filter(row => {
      const trimmed = row.trim();
      return trimmed && !trimmed.includes('#');
    });

    rows.forEach((row) => {
      const parts = row.split('|').map(p => p.trim());
      const label = parts.find(p => p.startsWith('Label:'))?.replace('Label:', '').trim();
      const valuePart = parts.find(p => p.startsWith('Value:'))?.replace('Value:', '').trim();
      const scopePart = parts.find(p => p.startsWith('Scope:'));
      const scope = scopePart?.includes('Variant') ? 'Variant' : 'Product';

      if (label && valuePart) {
        const [namespace, key] = valuePart.split('.').map((segment) => segment.trim());
        if (namespace && key) {
          config[label] = { namespace, key, scope };
        }
      }
    });

    return config;
  }

  /**
   * Parse variant JSON metafield refs from section/theme settings.
   * One namespace.key per line, or separated by ||. Order is try-first wins per cell.
   */
  parseVariantSpecsMetafieldRefs() {
    const raw = (this.variantSpecsRefFromDom || this.settings.variant_specs_metafield || '').trim();
    if (!raw) return [];
    const parts = raw
      .split(/\r?\n|\|\|/)
      .map((line) => line.trim())
      .filter(Boolean);
    const seen = new Set();
    const refs = [];
    for (const part of parts) {
      const firstDot = part.indexOf('.');
      if (firstDot <= 0 || firstDot === part.length - 1) continue;
      const namespace = part.slice(0, firstDot).trim();
      const key = part.slice(firstDot + 1).trim();
      if (!namespace || !key) continue;
      const dedupe = `${namespace}.${key}`;
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      refs.push({ namespace, key });
    }
    return refs;
  }

  // Fetch product metafields via GraphQL Storefront API
  async fetchProductMetafields(productId) {
    const variantRefs = this.parseVariantSpecsMetafieldRefs();
    let identifiers;

    if (variantRefs.length > 0) {
      identifiers = variantRefs.map((r) => ({ namespace: r.namespace, key: r.key }));
    } else {
      identifiers = [];
      if (this.settings.metafields) {
        const metafieldsConfig = this.parseMetafieldsConfig();
        identifiers = Object.values(metafieldsConfig).map((config) => ({
          namespace: config.namespace,
          key: config.key
        }));
      }
    }

    const compareNameRef = { namespace: 'custom', key: 'product_name_beginning' };
    const compareVariantImagesRef = { namespace: 'custom', key: 'images' };
    const seenKeys = new Set(identifiers.map((id) => `${id.namespace}.${id.key}`));
    if (!seenKeys.has('custom.product_name_beginning')) {
      identifiers.push(compareNameRef);
      seenKeys.add('custom.product_name_beginning');
    }
    if (!seenKeys.has('custom.images')) {
      identifiers.push(compareVariantImagesRef);
      seenKeys.add('custom.images');
    }
    if (!seenKeys.has('custom.dimensions_image')) {
      identifiers.push({ namespace: 'custom', key: 'dimensions_image' });
      seenKeys.add('custom.dimensions_image');
    }

    if (identifiers.length === 0) return null;

    // Build GraphQL query
    const metafieldsIdentifiers = identifiers.map((id) => 
      `{namespace: "${id.namespace}", key: "${id.key}"}`
    ).join(', ');

    const metafieldsSelection = `
            namespace
            key
            value
            type
            reference {
              __typename
              ... on MediaImage {
                image {
                  url
                }
              }
              ... on GenericFile {
                url
              }
            }
            references(first: 1) {
              edges {
                node {
                  __typename
                  ... on MediaImage {
                    image {
                      url
                    }
                  }
                  ... on GenericFile {
                    url
                  }
                }
              }
            }`;

    const query = `
      query getProductMetafields($id: ID!) {
        product(id: $id) {
          id
          metafields(identifiers: [${metafieldsIdentifiers}]) {${metafieldsSelection}
          }
          variants(first: 250) {
            edges {
              node {
                id
                metafields(identifiers: [${metafieldsIdentifiers}]) {${metafieldsSelection}
                }
              }
            }
          }
        }
      }
    `;

    try {
      // Convert product ID to GID format if needed
      let productGid = productId;
      if (typeof productId === 'number' || (typeof productId === 'string' && !productId.startsWith('gid://'))) {
        productGid = `gid://shopify/Product/${productId}`;
      }

      const response = await fetch('/api/2026-04/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '5293eae725aa79a73673527598c89e11'
        },
        body: JSON.stringify({
          query: query,
          variables: {
            id: productGid
          }
        })
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        return;
      }

      const productData = data.data?.product;
      if (!productData) {
        return null;
      }

      // Filter out null metafields
      if (productData.metafields) {
        productData.metafields = productData.metafields.filter(mf => mf !== null);
      }
      if (productData.variants?.edges?.length) {
        productData.variants.edges.forEach((edge) => {
          const node = edge?.node;
          if (node?.metafields) {
            node.metafields = node.metafields.filter((mf) => mf !== null);
          }
        });
      }

      return productData;
    } catch (error) {
      console.error('Error fetching metafields:', error);
      return null;
    }
  }

  parseVariantSpecsJsonPayload(raw) {
    if (raw == null || raw === '') return null;
    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return null;
      }
    }
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }
    if (Array.isArray(parsed)) {
      if (parsed.length === 1 && typeof parsed[0] === 'string') {
        try {
          const inner = JSON.parse(parsed[0]);
          if (Array.isArray(inner)) return inner;
        } catch {
          /* keep parsed */
        }
      }
      return parsed;
    }
    if (parsed && typeof parsed === 'object') {
      const keys = ['specifications', 'specification', 'rows', 'data', 'specs'];
      for (const k of keys) {
        if (parsed[k] == null) continue;
        const next = parsed[k];
        if (Array.isArray(next)) return next;
        if (typeof next === 'object' && !Array.isArray(next)) {
          const inner = this.parseVariantSpecsJsonPayload(next);
          if (Array.isArray(inner)) return inner;
        }
      }
    }
    return null;
  }

  /**
   * Turn storefront JSON (same string as pdp--variant-specs-json-value) into an array of row objects.
   * Handles wrapped objects and flat { "Label": "value" } maps.
   */
  normalizeVariantSpecsRowsForHydration(root) {
    const fromPayload = this.parseVariantSpecsJsonPayload(root);
    if (Array.isArray(fromPayload) && fromPayload.length > 0) return fromPayload;
    let p = root;
    if (typeof p === 'string') {
      try {
        p = JSON.parse(p);
      } catch {
        return null;
      }
    }
    if (typeof p === 'string') {
      try {
        p = JSON.parse(p);
      } catch {
        return null;
      }
    }
    if (p && typeof p === 'object' && !Array.isArray(p)) {
      const entries = Object.entries(p);
      const allPrimitive =
        entries.length > 0 &&
        entries.every(([, v]) => v === null || ['string', 'number', 'boolean'].includes(typeof v));
      if (allPrimitive) {
        return entries.map(([k, v]) => ({ specification: k, value: v }));
      }
    }
    return Array.isArray(fromPayload) ? fromPayload : null;
  }

  pickCompareSpecLabel(row) {
    if (row == null || typeof row !== 'object') return '';
    const labelRaw =
      row.specification ??
      row.Specification ??
      row.name ??
      row.Name ??
      row.label ??
      row.Label ??
      row.title ??
      row.Title ??
      row.heading ??
      row.Heading ??
      row.metric ??
      row.Metric;
    return labelRaw != null ? String(labelRaw).trim() : '';
  }

  pickCompareSpecValue(row) {
    if (row == null || typeof row !== 'object') return '';
    const valueRaw = row.value ?? row.Value ?? row.content ?? row.Content ?? row.reading ?? row.Reading;
    if (valueRaw == null) return '';
    if (typeof valueRaw === 'object') return JSON.stringify(valueRaw);
    return String(valueRaw);
  }

  pickCompareSpecTooltip(row) {
    if (row == null || typeof row !== 'object') return '';
    const tooltipRaw =
      row.tooltip ??
      row.Tooltip ??
      row.description ??
      row.Description ??
      row.help ??
      row.Help ??
      row.help_text ??
      row.helpText ??
      row.info ??
      row.Info ??
      row.hint ??
      row.Hint;
    if (tooltipRaw == null) return '';
    const tooltipText = String(tooltipRaw).trim();
    if (!tooltipText || tooltipText === 'YOUR_TOOLTIP_HERE' || tooltipText.includes('YOUR_TOOLTIP_HERE')) {
      return '';
    }
    return tooltipText;
  }

  escapeHtmlCompare(str) {
    if (str == null) return '';
    const el = document.createElement('div');
    el.textContent = String(str);
    return el.innerHTML;
  }

  hydrateSpecsRowsFromSeedIfEmpty() {
    if (!this.compareTable || !this.container) return;
    const tbody = this.compareTable.querySelector('tbody');
    if (!tbody) return;

    const seedEl = this.container.querySelector('script[data-compare-specs-seed]');
    if (!seedEl) return;
    const raw = (seedEl.textContent || '').trim();
    if (!raw || raw === 'null') return;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    let rows;
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0] != null &&
      typeof parsed[0] === 'object'
    ) {
      rows = parsed;
    } else {
      rows = this.normalizeVariantSpecsRowsForHydration(parsed);
    }
    if (!rows || rows.length === 0) return;

    const existing = tbody.querySelectorAll('tr.compare-table__row');
    const specRowsExisting = Array.from(existing).filter(
      (tr) => !tr.classList.contains('compare-table__row--available-options')
    );
    const firstLabel =
      specRowsExisting[0]?.querySelector('.compare-table__cell--label span')?.textContent?.trim() ??
      '';
    const liquidComplete = specRowsExisting.length === rows.length && firstLabel.length > 0;
    if (liquidComplete) {
      if (!tbody.querySelector('tr.compare-table__row--available-options')) {
        this.ensureAvailableOptionsRow();
      }
      this.updateCompareRowVisibility();
      return;
    }

    this.renderCompareTbodySpecRows(rows);
  }

  renderCompareTbodySpecRows(rows) {
    if (!this.compareTable || !rows?.length) return;
    const tbody = this.compareTable.querySelector('tbody');
    if (!tbody) return;

    const specRowsExisting = tbody.querySelectorAll(
      'tr.compare-table__row:not(.compare-table__row--available-options)'
    );
    specRowsExisting.forEach((tr) => tr.remove());
    tbody.querySelector('tr.compare-table__row--available-options')?.remove();

    const headerRow = this.compareTable.querySelector('thead tr:first-child');
    const productColumns = headerRow
      ? headerRow.querySelectorAll('.compare-table__heading--product')
      : [];
    const numProductCols = productColumns.length;
    const hasAddCol = !!this.compareTable.querySelector('.compare-table__heading--add');
    let currentColIndex = 0;
    productColumns.forEach((col, i) => {
      if (col.classList.contains('compare-table__heading--current')) currentColIndex = i;
    });

    rows.forEach((row) => {
      if (row == null || typeof row !== 'object') return;
      const label = this.pickCompareSpecLabel(row);
      const val = this.pickCompareSpecValue(row);
      if (!val || !String(val).trim()) return;

      const tr = document.createElement('tr');
      tr.className = 'compare-table__row card-product__specs-table-row';

      const th = document.createElement('th');
      th.className =
        'compare-table__cell compare-table__cell--label compare-table__cell--sticky card-product__specs-table-row-heading';
      const span = document.createElement('span');
      span.innerHTML = this.escapeHtmlCompare(label);
      th.appendChild(span);
      const tip = this.pickCompareSpecTooltip(row);
      if (tip) {
        const tipWrap = document.createElement('span');
        tipWrap.className = 'compare-table__tooltip';
        const tipIcon = document.createElement('span');
        tipIcon.className = 'compare-table__tooltip-icon';
        tipIcon.setAttribute('aria-label', 'More info');
        tipIcon.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0582/1136/9133/files/Tool-Tip_Button.png?v=1779373905" width="16" height="16" alt="More info">';
        const tipContent = document.createElement('span');
        tipContent.className = 'compare-table__tooltip-content';
        tipContent.textContent = tip;
        tipWrap.appendChild(tipIcon);
        tipWrap.appendChild(tipContent);
        th.appendChild(tipWrap);
      }
      tr.appendChild(th);

      for (let i = 0; i < numProductCols; i++) {
        const td = document.createElement('td');
        const isCurrentCol = productColumns[i]?.classList.contains('compare-table__heading--current');
        td.className =
          'compare-table__cell compare-table__cell--value card-product__specs-table-row-cell' +
          (isCurrentCol ? ' compare-table__cell--current' : '') +
          (i === 0 ? ' is-active' : '');
        td.dataset.columnIndex = String(i);
        if (i === currentColIndex) {
          const rte = document.createElement('div');
          rte.className = 'rte';
          rte.innerHTML = val || '&nbsp;';
          td.appendChild(rte);
        } else {
          td.innerHTML = '&nbsp;';
        }
        tr.appendChild(td);
      }

      if (hasAddCol) {
        const emptyTd = document.createElement('td');
        emptyTd.className = 'compare-table__cell compare-table__cell--empty';
        emptyTd.innerHTML = '&nbsp;';
        tr.appendChild(emptyTd);
      }

      tbody.appendChild(tr);
    });

    this.ensureAvailableOptionsRow();
    this.totalSlides = this.compareTable.querySelectorAll('.compare-table__heading--product').length;
    this.initMobileTableSlider();
    this.updateCompareRowVisibility();
  }

  ensureAvailableOptionsRow() {
    if (!this.compareTable) return;
    const tbody = this.compareTable.querySelector('tbody');
    if (!tbody) return;

    tbody.querySelector('tr.compare-table__row--available-options')?.remove();

    const headerRow = this.compareTable.querySelector('thead tr:first-child');
    const productColumns = headerRow
      ? headerRow.querySelectorAll('.compare-table__heading--product')
      : [];
    const hasAddCol = !!this.compareTable.querySelector('.compare-table__heading--add');

    const tr = document.createElement('tr');
    tr.className =
      'compare-table__row card-product__specs-table-row compare-table__row--available-options';

    const th = document.createElement('th');
    th.className =
      'compare-table__cell compare-table__cell--label compare-table__cell--sticky card-product__specs-table-row-heading';
    const labelSpan = document.createElement('span');
    labelSpan.textContent = 'Available Options';
    th.appendChild(labelSpan);
    tr.appendChild(th);

    productColumns.forEach((columnTh, index) => {
      const td = document.createElement('td');
      td.className =
        'compare-table__cell compare-table__cell--value card-product__specs-table-row-cell' +
        (columnTh.classList.contains('compare-table__heading--current') ? ' compare-table__cell--current' : '') +
        (index === 0 ? ' is-active' : '');
      td.dataset.columnIndex = String(index);
      td.innerHTML = '&nbsp;';
      tr.appendChild(td);
    });

    if (hasAddCol) {
      const emptyTd = document.createElement('td');
      emptyTd.className = 'compare-table__cell compare-table__cell--empty';
      emptyTd.innerHTML = '&nbsp;';
      tr.appendChild(emptyTd);
    }

    tbody.appendChild(tr);
    this.populateAvailableOptionsRow(tr, productColumns).catch((err) => console.error(err));
  }

  async populateAvailableOptionsRow(row, productColumns) {
    if (!row || !productColumns?.length) return;

    for (let index = 0; index < productColumns.length; index++) {
      const columnTh = productColumns[index];
      const td = row.querySelector(`td[data-column-index="${index}"]`);
      if (!td) continue;

      const handle = columnTh.dataset.productHandle;
      if (!handle) continue;

      try {
        const product = await this.fetchProductJson(handle);
        if (!product?.variants?.length) continue;

        const variantId = columnTh.dataset.variantId;
        const variant =
          product.variants.find((v) => String(v.id) === String(variantId)) || product.variants[0];
        const column = this.buildColumnPayload(product, variant, null);
        const options = this.formatAvailableOptions(column);
        td.innerHTML = options ? `<div class="rte">${options}</div>` : '&nbsp;';
      } catch {
        /* keep placeholder */
      }
    }

    this.updateCompareRowVisibility();
  }

  findSpecRowValue(rows, rowLabel) {
    if (!Array.isArray(rows) || !rowLabel) return '';
    const needle = rowLabel.trim().toLowerCase();
    for (const row of rows) {
      const label = this.pickCompareSpecLabel(row);
      if (!label || label.toLowerCase() !== needle) continue;
      const val = this.pickCompareSpecValue(row);
      if (val) return val;
    }
    return '';
  }

  loadCompareEmbeddedVariantSpecs() {
    const el = this.container?.querySelector('script[data-compare-variant-specs]');
    if (!el?.textContent) return {};
    try {
      const parsed = JSON.parse(el.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  getCompareEmbeddedVariantSpecsRows(variantId) {
    const raw = this.compareEmbeddedVariantSpecs?.[String(variantId)];
    if (raw == null || raw === 'null') return null;
    return this.normalizeVariantSpecsRowsForHydration(raw);
  }

  getVariantSpecsRowsFromMetafields(metafieldsData, variantId) {
    if (!metafieldsData?.variants?.edges?.length) return null;
    const want = String(variantId);
    const gidTail = (gid) => {
      if (!gid) return '';
      const gidString = String(gid);
      return gidString.includes('/') ? gidString.split('/').pop() : gidString;
    };
    const refs = this.parseVariantSpecsMetafieldRefs();
    if (!refs.length) return null;

    for (const edge of metafieldsData.variants.edges) {
      const node = edge?.node;
      if (!node || gidTail(node.id) !== want) continue;
      if (!node.metafields?.length) continue;
      for (const ref of refs) {
        const mf = node.metafields.find(
          (metafield) => metafield && metafield.namespace === ref.namespace && metafield.key === ref.key
        );
        if (!mf || mf.value == null || mf.value === '') continue;
        const rows = this.normalizeVariantSpecsRowsForHydration(mf.value);
        if (rows?.length) return rows;
      }
    }
    return null;
  }

  getVariantSpecsRowsFromLiquidMap(metafieldsData, variantId) {
    const map = metafieldsData?.compareVariantSpecs;
    if (!map || variantId == null) return null;
    const raw = map[String(variantId)];
    if (raw == null || raw === 'null') return null;
    return this.normalizeVariantSpecsRowsForHydration(raw);
  }

  getVariantSpecsRowsForId(variantId, metafieldsData) {
    return (
      this.getCompareEmbeddedVariantSpecsRows(variantId) ||
      this.getVariantSpecsRowsFromLiquidMap(metafieldsData, variantId) ||
      this.getVariantSpecsRowsFromMetafields(metafieldsData, variantId)
    );
  }

  getVariantJsonSpecCellContent(rowLabel, column) {
    const lockedVariantId =
      column.preferredVariantId != null && column.preferredVariantId !== ''
        ? String(column.preferredVariantId)
        : column.variantId != null
          ? String(column.variantId)
          : '';

    // Liquid alternate template (works on Pro/EU without Storefront token)
    if (lockedVariantId) {
      const liquidRows = this.getVariantSpecsRowsFromLiquidMap(column.metafieldsData, lockedVariantId);
      const liquidVal = this.findSpecRowValue(liquidRows, rowLabel);
      if (liquidVal) {
        return `<div class="rte">${liquidVal}</div>`;
      }
    }

    const refs = this.parseVariantSpecsMetafieldRefs();
    if (!refs.length) return '';
    const data = column.metafieldsData;
    if (!data?.variants?.edges?.length) return '';

    const pd = column.productData || {};
    const restVariants = pd.variants || [];
    const preferredId =
      lockedVariantId || (restVariants[0]?.id != null ? String(restVariants[0].id) : '');

    const gidTail = (gid) => {
      if (!gid) return '';
      const gidString = String(gid);
      return gidString.includes('/') ? gidString.split('/').pop() : gidString;
    };

    let nodes = data.variants.edges.map((e) => e.node).filter(Boolean);
    if (lockedVariantId) {
      const filtered = nodes.filter((variantNode) => gidTail(variantNode.id) === lockedVariantId);
      if (filtered.length) nodes = filtered;
    }
    nodes = [...nodes].sort((a, b) => {
      const at = gidTail(a.id);
      const bt = gidTail(b.id);
      if (preferredId && at === preferredId) return -1;
      if (preferredId && bt === preferredId) return 1;
      return 0;
    });

    for (const variant of nodes) {
      if (!variant?.metafields) continue;
      for (const ref of refs) {
        const mf = variant.metafields.find(
          (metafield) => metafield && metafield.namespace === ref.namespace && metafield.key === ref.key
        );
        if (!mf || mf.value == null || mf.value === '') continue;
        const rows = this.normalizeVariantSpecsRowsForHydration(mf.value);
        const val = this.findSpecRowValue(rows, rowLabel);
        if (val) {
          return `<div class="rte">${val}</div>`;
        }
      }
    }
    return '';
  }

  formatCompareVariantPrice(column) {
    const variantId = String(column?.preferredVariantId ?? column?.variantId ?? '');
    const variants = column?.productData?.variants || [];
    const variant = variants.find((v) => String(v?.id) === variantId);
    let rawPrice = column?.price;
    if (rawPrice == null || rawPrice === '') rawPrice = variant?.price;
    if (rawPrice == null || rawPrice === '') return '';
    const rawPriceStr = String(rawPrice).trim();
    if (!rawPriceStr) return '';
    const hasDecimal = rawPriceStr.includes('.');
    const numeric = Number(rawPriceStr);
    if (!Number.isFinite(numeric) || numeric <= 0) return '';
    const amount = hasDecimal ? numeric : numeric / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency
    }).format(amount);
  }

  formatAvailableOptions(column) {
    const product = column?.productData;
    const variants = product?.variants;
    if (!variants?.length || column?.hasOnlyDefaultVariant) return '';

    const titles = variants
      .map((variant) => (variant?.title != null ? String(variant.title).trim() : ''))
      .filter((title) => title.length > 0);

    if (titles.length === 0) return '';
    return titles.map((title) => this.escapeHtmlCompare(title)).join(', ');
  }

  // Get cell content based on row label (column = variant + product payload)
  getCellContent(rowLabel, column, metafieldsConfig) {
    if (!rowLabel) return '';

    if (rowLabel.trim() === 'Available Options') {
      const options = this.formatAvailableOptions(column);
      return options ? `<div class="rte">${options}</div>` : '';
    }

    if (
      this.parseVariantSpecsMetafieldRefs().length > 0 ||
      column.metafieldsData?.compareVariantSpecs
    ) {
      return this.getVariantJsonSpecCellContent(rowLabel, column);
    }

    // Handle metafields (textarea configuration)
    const config = metafieldsConfig[rowLabel];
    if (!config) {
      return '';
    }

    const metafieldsData = column.metafieldsData;
    if (!metafieldsData) {
      return '';
    }

    if (config.scope === 'Variant') {
      const variantMetafields = [];
      const want =
        column.preferredVariantId != null && String(column.preferredVariantId).trim() !== ''
          ? String(column.preferredVariantId).trim()
          : '';
      const gidTail = (gid) => {
        if (!gid) return '';
        const gidString = String(gid);
        return gidString.includes('/') ? gidString.split('/').pop() : gidString;
      };
      if (metafieldsData.variants?.edges) {
        metafieldsData.variants.edges.forEach((edge) => {
          const variant = edge.node;
          if (want && gidTail(variant.id) !== want) return;
          if (variant.metafields) {
            variant.metafields.forEach((mf) => {
              if (mf.namespace === config.namespace && mf.key === config.key && mf.value) {
                const formatted = this.formatMetafieldValue(mf, config);
                if (formatted && !variantMetafields.includes(formatted)) {
                  variantMetafields.push(formatted);
                }
              }
            });
          }
        });
      }
      return variantMetafields.length > 0 ? variantMetafields.join(', ') : '';
    } else {
      // Handle product-scoped metafields
      if (!metafieldsData.metafields || metafieldsData.metafields.length === 0) {
        return '';
      }

      const productMetafield = metafieldsData.metafields.find(
        (mf) => mf && mf.namespace === config.namespace && mf.key === config.key
      );
      
      if (productMetafield && productMetafield.value !== null && productMetafield.value !== undefined && productMetafield.value !== '') {
        const formatted = this.formatMetafieldValue(productMetafield, config);
        return formatted || '';
      }
      
      return '';
    }
  }

  // Format metafield value based on type
  formatMetafieldValue(metafield, config) {
    let value = metafield.value;
    const type = metafield.type || '';

    if (!value) return '';

    // Handle list types
    if (type.startsWith('list.')) {
      const listType = type.replace('list.', '');
      
      // Parse JSON string if value is a string
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // If parsing fails, treat as single value
          return this.formatSingleMetafieldValue(value, listType);
        }
      }
      
      if (Array.isArray(value)) {
        return value.map((item) => this.formatSingleMetafieldValue(item, listType)).join(', ');
      }
      
      // If it's not an array after parsing, treat as single value
      return this.formatSingleMetafieldValue(value, listType);
    }

    return this.formatSingleMetafieldValue(value, type);
  }

  // Format a single metafield value
  formatSingleMetafieldValue(value, type) {
    if (!value) return '';

    switch (type) {
      case 'url':
        return `<a href="${value}" class="link" target="_blank">${value}</a>`;
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date_time':
        return new Date(value).toLocaleString();
      case 'color':
        return `<div class="metafield-color" style="background-color: ${value}"></div>`;
      case 'file_reference':
        if (value.media_type === 'image') {
          return `<img src="${value.url || value.preview_image?.url || ''}" height="${value.height || ''}" width="${value.width || ''}" loading="lazy" alt="${value.alt || ''}" />`;
        }
        return `<a href="${value.url || ''}" download class="text-button">${value.alt || 'Download'}</a>`;
      case 'collection_reference':
      case 'page_reference':
      case 'variant_reference':
      case 'product_reference':
        return `<a href="${value.url || ''}" class="link" target="_blank">${value.title || value.name || ''}</a>`;
      case 'rich_text_field':
        return value;
      default:
        return String(value);
    }
  }
}

// Compare modal: product select → variant select (variants omit those already in the table)
class ProductCompareModal {
  constructor() {
    this.modal = document.querySelector('.product-compare-modal');
    if (!this.modal) return;

    this.overlay = this.modal.querySelector('.product-compare-modal__overlay');
    this.closeBtn = this.modal.querySelector('.product-compare-modal__close');
    this.productSelect = this.modal.querySelector('#product-compare-product');
    this.variantSelect = this.modal.querySelector('#product-compare-variant');
    this.variantSelectWrap = this.modal.querySelector('#product-compare-variant-wrap');
    this.addBtn = this.modal.querySelector('.product-compare-modal__add-btn');

    this.productsCatalog = [];
    const dataScript = this.modal.querySelector('#product-compare-data');
    if (dataScript?.textContent) {
      try {
        const parsed = JSON.parse(dataScript.textContent);
        this.productsCatalog = Array.isArray(parsed) ? parsed : [];
      } catch {
        this.productsCatalog = [];
      }
    }

    /** Products with variant list after filtering already-compared IDs */
    this.filteredProducts = [];

    this.setupEventListeners();
  }

  getCompareVariantIds() {
    const ids = new Set();
    document.querySelectorAll('.compare-table__heading--product').forEach((header) => {
      const vid = header.dataset.variantId;
      if (vid) ids.add(String(vid));
    });
    return ids;
  }

  getFilteredProducts() {
    const inCompare = this.getCompareVariantIds();
    return this.productsCatalog
      .map((p) => ({
        ...p,
        variants: (p.variants || []).filter((v) => v && !inCompare.has(String(v.id)))
      }))
      .filter((p) => p.variants.length > 0);
  }

  fillProductSelect() {
    if (!this.productSelect) return;
    const ph = this.productSelect.dataset.placeholderOption || 'Select a product';

    this.productSelect.innerHTML = '';
    const opt0 = document.createElement('option');
    opt0.value = '';
    opt0.textContent = ph;
    this.productSelect.appendChild(opt0);

    this.filteredProducts.forEach((p) => {
      const opt = document.createElement('option');
      opt.value = p.handle;
      opt.textContent = p.title;
      this.productSelect.appendChild(opt);
    });

    if (this.productSelect.options.length === 1) {
      opt0.disabled = true;
      opt0.textContent = 'No additional products';
    } else {
      opt0.disabled = false;
      opt0.textContent = ph;
    }
  }

  setVariantRowVisible(show) {
    if (this.variantSelectWrap) {
      this.variantSelectWrap.hidden = !show;
    }
  }

  resetVariantSelect() {
    if (!this.variantSelect) return;
    this.setVariantRowVisible(false);
    const ph = this.variantSelect.dataset.placeholderOption || 'Select a variant';
    this.variantSelect.innerHTML = '';
    const opt0 = document.createElement('option');
    opt0.value = '';
    opt0.textContent = ph;
    this.variantSelect.appendChild(opt0);
    this.variantSelect.disabled = true;
  }

  fillVariantSelectForProduct(handle) {
    if (!this.variantSelect) return;

    const product = this.filteredProducts.find((p) => p.handle === handle);
    if (!product || !product.variants.length) {
      this.setVariantRowVisible(false);
      this.variantSelect.disabled = true;
      if (this.addBtn) this.addBtn.disabled = true;
      return;
    }

    const ph = this.variantSelect.dataset.placeholderOption || 'Select a variant';

    if (product.variants.length === 1) {
      const v = product.variants[0];
      this.setVariantRowVisible(false);
      this.variantSelect.innerHTML = '';
      const opt = document.createElement('option');
      opt.value = String(v.id);
      opt.textContent = [v.title || ''].join('').trim() || `Variant ${v.id}`;
      opt.selected = true;
      this.variantSelect.appendChild(opt);
      this.variantSelect.disabled = true;
      if (this.addBtn) this.addBtn.disabled = false;
      return;
    }

    this.setVariantRowVisible(true);
    this.variantSelect.innerHTML = '';
    const opt0 = document.createElement('option');
    opt0.value = '';
    opt0.textContent = ph;
    this.variantSelect.appendChild(opt0);

    product.variants.forEach((v) => {
      const opt = document.createElement('option');
      opt.value = String(v.id);
      opt.textContent = [v.title || ''].join('').trim() || `Variant ${v.id}`;
      this.variantSelect.appendChild(opt);
    });

    this.variantSelect.disabled = false;
    if (this.addBtn) this.addBtn.disabled = true;
  }

  setupEventListeners() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.compare-table__add-button')) {
        e.preventDefault();
        this.open();
      }
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    if (this.productSelect) {
      this.productSelect.addEventListener('change', () => {
        const handle = String(this.productSelect.value || '').trim();
        if (!handle) {
          this.resetVariantSelect();
          if (this.addBtn) this.addBtn.disabled = true;
          return;
        }
        this.fillVariantSelectForProduct(handle);
      });
    }

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', () => {
        const hasVariant = !!String(this.variantSelect.value || '').trim();
        if (this.addBtn) this.addBtn.disabled = !hasVariant;
      });
    }

    if (this.addBtn) {
      this.addBtn.addEventListener('click', () => this.addSelectedProduct());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    if (isCompareLimitReached().atLimit) {
      return;
    }
    this.filteredProducts = this.getFilteredProducts();
    this.fillProductSelect();
    this.resetVariantSelect();
    if (this.addBtn) this.addBtn.disabled = true;
    this.modal.classList.add('is-open');
    document.body.classList.add('modal-open');
  }

  close() {
    this.modal.classList.remove('is-open');
    document.body.classList.remove('modal-open');
    if (this.productSelect && this.productSelect.options.length) {
      this.productSelect.selectedIndex = 0;
    }
    this.resetVariantSelect();
    if (this.addBtn) this.addBtn.disabled = true;
  }

  addSelectedProduct() {
    const handle = this.productSelect && String(this.productSelect.value || '').trim();
    const variantId = this.variantSelect && String(this.variantSelect.value || '').trim();

    if (!handle || !variantId) return;

    const event = new CustomEvent('compare:add-product', {
      detail: { variantId, handle },
      bubbles: true
    });
    this.modal.dispatchEvent(event);

    this.close();
  }
}

// Sync add button icon height with product images
function syncAddButtonHeight() {
  const productImage = document.querySelector('.compare-table__product-image');
  const addButtonIcon = document.querySelector('.compare-table__heading--add .compare-table__add-button-icon');
  
  if (productImage && addButtonIcon) {
    const imageHeight = productImage.getBoundingClientRect().height;
    if (imageHeight > 0) {
      addButtonIcon.style.height = `${imageHeight}px`;
      addButtonIcon.style.width = `${imageHeight}px`;
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductComparisonTable();
  new ProductCompareModal();
  
  const productImage = document.querySelector('.compare-table__product-image');
  
  if (productImage) {
    // Use ResizeObserver for reliable size detection
    const resizeObserver = new ResizeObserver(() => {
      syncAddButtonHeight();
    });
    resizeObserver.observe(productImage);
    
    // Also sync after image loads
    productImage.addEventListener('load', syncAddButtonHeight);
  }
  
  // Update on resize as backup
  window.addEventListener('resize', syncAddButtonHeight);
});
